// Field groups for marker authoring. Presentational only: each group renders
// cells of the form's one two-column Grid and reports edits back through
// draft patches. No Section chrome (UI-LAW §4c Authoring): a group returns a
// fragment of cells — two per row, or one `GridItem span="full"` — so every
// row of the page lands on the same two-column rhythm. An empty `<span />`
// holds the second cell when a row has one control.

import type { ReactNode } from "react";

import {
  Button,
  ChipInput,
  Grid,
  GridItem,
  HelpTip,
  Inline,
  Select,
  Stack,
  TextInput,
} from "../ui";
import { TraceNodeLocatorPicker } from "./TraceNodeLocatorPicker";
import type {
  AssertionCaseDraft,
  AssertionMode,
  ConditionMatch,
  Locator,
  LocatorDraft,
  MarkerOutput,
  ReviewPolicy,
  Verdict,
} from "./markerAuthoringDraft";

const verdictOptions: Verdict[] = ["pass", "warn", "fail"];

export interface OutputSpecDraft {
  categoryValues: string[];
  numberMax: string;
  numberMin: string;
}

export interface OutputSpecFieldsProps {
  busy: boolean;
  draft: OutputSpecDraft;
  onChange: (patch: Partial<OutputSpecDraft>) => void;
  output: MarkerOutput;
}

// The controls that narrow the chosen output shape. They sit on the same row
// as the output type so the shape and its definition read as one choice.
export function OutputSpecFields({
  busy,
  draft,
  onChange,
  output,
}: OutputSpecFieldsProps) {
  if (output === "boolean") {
    // A boolean is always true/false: nothing to define.
    return null;
  }
  if (output === "number") {
    return (
      <>
        <TextInput
          disabled={busy}
          label="Minimum"
          placeholder="1"
          required
          step="any"
          type="number"
          value={draft.numberMin}
          onChange={(event) => {
            onChange({ numberMin: event.currentTarget.value });
          }}
        />
        <TextInput
          disabled={busy}
          label="Maximum"
          placeholder="5"
          required
          step="any"
          type="number"
          value={draft.numberMax}
          onChange={(event) => {
            onChange({ numberMax: event.currentTarget.value });
          }}
        />
      </>
    );
  }
  return (
    <GridItem span="full">
      <ChipInput
        addLabel="Add category"
        disabled={busy}
        help={
          <HelpTip label="About categories">
            {"The judge must return exactly one of these, spelled the same " +
              "way, so name each category in the Rubric. Press Enter or " +
              "Add after each one."}
          </HelpTip>
        }
        label="Categories"
        placeholder="billing"
        required
        values={draft.categoryValues}
        onChange={(categoryValues) => {
          onChange({ categoryValues });
        }}
      />
    </GridItem>
  );
}

export interface LocatorFieldsProps {
  agentId?: string;
  busy: boolean;
  draft: LocatorDraft;
  onChange: (patch: Partial<LocatorDraft>) => void;
}

export function ScopeSelect({ busy, draft, onChange }: LocatorFieldsProps) {
  return (
    <Select
      disabled={busy}
      help={
        <HelpTip label="About scope">
          {"What the marker reads. The whole transcript by default, or " +
            "only a range of utterances, characters, audio time, or one " +
            "trace node."}
        </HelpTip>
      }
      label="Scope"
      value={draft.kind}
      onChange={(event) => {
        onChange({ kind: event.currentTarget.value as Locator["kind"] });
      }}
    >
      <option value="whole_transcript">Entire transcript</option>
      <option value="utterance_range">Utterance range</option>
      <option value="char_span">Character range</option>
      <option value="audio_span">Time range</option>
      <option value="trace_node">Trace node</option>
    </Select>
  );
}

// The scope's own inputs. Null for the default whole-transcript scope, so
// the details row below the settings row exists only when it has content.
export function ScopeParams({
  agentId,
  busy,
  draft,
  onChange,
}: LocatorFieldsProps) {
  if (draft.kind === "audio_span") {
    return (
      <>
        <NumberField
          label="Start time (ms)"
          value={draft.startMs}
          busy={busy}
          onChange={(startMs) => {
            onChange({ startMs });
          }}
        />
        <NumberField
          label="End time (ms)"
          value={draft.endMsExclusive}
          busy={busy}
          onChange={(endMsExclusive) => {
            onChange({ endMsExclusive });
          }}
        />
      </>
    );
  }
  if (draft.kind === "utterance_range") {
    return (
      <>
        <NumberField
          label="First utterance"
          value={draft.startIndex}
          busy={busy}
          onChange={(startIndex) => {
            onChange({ startIndex });
          }}
        />
        <NumberField
          label="End utterance"
          value={draft.endIndexExclusive}
          busy={busy}
          onChange={(endIndexExclusive) => {
            onChange({ endIndexExclusive });
          }}
        />
      </>
    );
  }
  if (draft.kind === "char_span") {
    return (
      <>
        <NumberField
          label="Utterance index"
          value={draft.utteranceIndex}
          busy={busy}
          required={false}
          onChange={(utteranceIndex) => {
            onChange({ utteranceIndex });
          }}
        />
        <NumberField
          label="First character"
          value={draft.startChar}
          busy={busy}
          onChange={(startChar) => {
            onChange({ startChar });
          }}
        />
        <NumberField
          label="End character"
          value={draft.endCharExclusive}
          busy={busy}
          onChange={(endCharExclusive) => {
            onChange({ endCharExclusive });
          }}
        />
      </>
    );
  }
  if (draft.kind === "trace_node") {
    return agentId ? (
      <GridItem span="full">
        <TraceNodeLocatorPicker
          agentId={agentId}
          disabled={busy}
          nodeId={draft.nodeId}
          onNodeIdChange={(nodeId) => {
            onChange({ nodeId });
          }}
        />
      </GridItem>
    ) : (
      <TextInput
        disabled={busy}
        help={
          <HelpTip label="About trace nodes">
            {"A span ID or an exact span name. Select an agent to browse " +
              "its sample traces instead of typing one."}
          </HelpTip>
        }
        label="Trace node"
        placeholder="tool.lookup_order"
        required
        value={draft.nodeId}
        onChange={(event) => {
          onChange({ nodeId: event.currentTarget.value });
        }}
      />
    );
  }
  return null;
}

export function hasScopeParams(draft: LocatorDraft): boolean {
  return draft.kind !== "whole_transcript";
}

interface NumberFieldProps {
  busy: boolean;
  label: string;
  onChange: (value: string) => void;
  required?: boolean;
  value: string;
}

function NumberField({
  busy,
  label,
  onChange,
  required = true,
  value,
}: NumberFieldProps) {
  return (
    <TextInput
      disabled={busy}
      label={label}
      min={0}
      required={required}
      step={1}
      type="number"
      value={value}
      onChange={(event) => {
        onChange(event.currentTarget.value);
      }}
    />
  );
}

export interface AssertionFieldsProps {
  busy: boolean;
  cases: AssertionCaseDraft[];
  categories: string[];
  defaultVerdict: Verdict;
  help?: ReactNode;
  mode: AssertionMode;
  onAdd: () => void;
  onCaseChange: (id: number, patch: Partial<AssertionCaseDraft>) => void;
  onDefaultChange: (verdict: Verdict) => void;
  onModeChange: (mode: AssertionMode) => void;
  onRemove: (id: number) => void;
  output: MarkerOutput;
}

export function ResultHandlingSelect({
  busy,
  help,
  mode,
  onModeChange,
}: Pick<AssertionFieldsProps, "busy" | "help" | "mode" | "onModeChange">) {
  return (
    <Select
      disabled={busy}
      help={help}
      label="Result handling"
      value={mode}
      onChange={(event) => {
        onModeChange(event.currentTarget.value as AssertionMode);
      }}
    >
      <option value="value">Record value only</option>
      <option value="verdict">Apply pass, warn, or fail</option>
    </Select>
  );
}

export function AssertionDefaultSelect({
  busy,
  defaultVerdict,
  onDefaultChange,
}: Pick<AssertionFieldsProps, "busy" | "defaultVerdict" | "onDefaultChange">) {
  return (
    <VerdictSelect
      disabled={busy}
      label="When no condition matches"
      value={defaultVerdict}
      onChange={onDefaultChange}
    />
  );
}

export function AssertionCaseList({
  busy,
  cases,
  categories,
  onAdd,
  onCaseChange,
  onRemove,
  output,
}: Omit<
  AssertionFieldsProps,
  "defaultVerdict" | "help" | "mode" | "onDefaultChange" | "onModeChange"
>) {
  return (
    <Stack gap={3}>
      {cases.map((item) => (
        <AssertionCaseFields
          key={item.id}
          busy={busy}
          categories={categories}
          draft={item}
          output={output}
          onChange={(patch) => {
            onCaseChange(item.id, patch);
          }}
          onRemove={() => {
            onRemove(item.id);
          }}
        />
      ))}
      <Inline>
        <Button disabled={busy} size="sm" variant="secondary" onClick={onAdd}>
          Add condition
        </Button>
      </Inline>
    </Stack>
  );
}

interface AssertionCaseFieldsProps {
  busy: boolean;
  categories: string[];
  draft: AssertionCaseDraft;
  onChange: (patch: Partial<AssertionCaseDraft>) => void;
  onRemove: () => void;
  output: MarkerOutput;
}

function AssertionCaseFields({
  busy,
  categories,
  draft,
  onChange,
  onRemove,
  output,
}: AssertionCaseFieldsProps) {
  const pickFromCategories = draft.match === "equals" && categories.length > 0;
  return (
    <Grid columns={4} gap={4}>
      {output === "number" ? (
        <Select
          disabled={busy}
          label="Comparison"
          value={draft.match}
          onChange={(event) => {
            onChange({ match: event.currentTarget.value as ConditionMatch });
          }}
        >
          <option value=">=">At least</option>
          <option value=">">Greater than</option>
          <option value="<=">At most</option>
          <option value="<">Less than</option>
          <option value="==">Equals</option>
          <option value="!=">Does not equal</option>
        </Select>
      ) : null}
      {output === "category" ? (
        <Select
          disabled={busy}
          label="Comparison"
          value={draft.match}
          onChange={(event) => {
            onChange({ match: event.currentTarget.value as ConditionMatch });
          }}
        >
          <option value="equals">Equals</option>
          <option value="in">Is one of</option>
          <option value="not_in">Is not one of</option>
        </Select>
      ) : null}
      {output === "boolean" ? (
        <Select
          disabled={busy}
          label="Value"
          value={draft.value}
          onChange={(event) => {
            onChange({ value: event.currentTarget.value });
          }}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Select>
      ) : pickFromCategories ? (
        <Select
          disabled={busy}
          label="Category"
          required
          value={draft.value}
          onChange={(event) => {
            onChange({ value: event.currentTarget.value });
          }}
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </Select>
      ) : (
        <TextInput
          disabled={busy}
          label={output === "number" ? "Value" : "Category values"}
          placeholder={
            output === "number"
              ? "3"
              : categories.length > 0
                ? categories.slice(0, 2).join(", ")
                : "billing, cancel"
          }
          required
          step={output === "number" ? "any" : undefined}
          type={output === "number" ? "number" : "text"}
          value={draft.value}
          onChange={(event) => {
            onChange({ value: event.currentTarget.value });
          }}
        />
      )}
      <VerdictSelect
        disabled={busy}
        label="Verdict"
        value={draft.verdict}
        onChange={(verdict) => {
          onChange({ verdict });
        }}
      />
      <Inline align="end">
        <Button
          disabled={busy}
          size="sm"
          variant="secondary"
          onClick={onRemove}
        >
          Remove
        </Button>
      </Inline>
    </Grid>
  );
}

interface VerdictSelectProps {
  disabled: boolean;
  label: string;
  onChange: (verdict: Verdict) => void;
  value: Verdict;
}

function VerdictSelect({
  disabled,
  label,
  onChange,
  value,
}: VerdictSelectProps) {
  return (
    <Select
      disabled={disabled}
      label={label}
      value={value}
      onChange={(event) => {
        onChange(event.currentTarget.value as Verdict);
      }}
    >
      {verdictOptions.map((verdict) => (
        <option key={verdict} value={verdict}>
          {verdict === "pass" ? "Pass" : verdict === "warn" ? "Warn" : "Fail"}
        </option>
      ))}
    </Select>
  );
}

export interface ReviewFieldsProps {
  busy: boolean;
  onPolicyChange: (policy: ReviewPolicy) => void;
  onSampleRateChange: (value: string) => void;
  policy: ReviewPolicy;
  sampleRate: string;
}

export function ReviewPolicySelect({
  busy,
  onPolicyChange,
  policy,
}: Pick<ReviewFieldsProps, "busy" | "onPolicyChange" | "policy">) {
  return (
    <Select
      disabled={busy}
      help={
        <HelpTip label="About human review">
          {"Route this marker's results into the Review queue: every " +
            "result, a random sample, or none. Reviewers confirm or " +
            "correct each Mark with a Label."}
        </HelpTip>
      }
      label="Send for human review"
      value={policy}
      onChange={(event) => {
        onPolicyChange(event.currentTarget.value as ReviewPolicy);
      }}
    >
      <option value="never">Never</option>
      <option value="always">Every result</option>
      <option value="sample">Sample</option>
    </Select>
  );
}

export function ReviewSampleField({
  busy,
  onSampleRateChange,
  sampleRate,
}: Pick<ReviewFieldsProps, "busy" | "onSampleRateChange" | "sampleRate">) {
  return (
    <TextInput
      disabled={busy}
      label="Sample percentage"
      max={100}
      min={1}
      placeholder="10"
      required
      step={1}
      type="number"
      value={sampleRate}
      onChange={(event) => {
        onSampleRateChange(event.currentTarget.value);
      }}
    />
  );
}
